import { BaseElement } from 'base';
import { runAction } from 'run-action';

class SelectField extends BaseElement {
  #handlers = new Set();
  #action = null;
  #initialValue = '';
  #initialFilled = false;
  #initialLabel = '';
  #initialLabelledby = null;
  #initialIcon = null;
  #locked = new Set();
  #frame = 0;
  #fitGutter = 0;
  #fitMin = 0;

  onConnect() {
    this.trigger = this.q('.select__trigger');
    this.modal = this.q('.select-modal');
    this.list = this.q('.select-modal__list');
    this.searchInput = this.q('.select-modal__search-input');
    this.emptyMessage = this.q('.select-modal__empty');
    this.hiddenInput = this.q('input[type="hidden"]');
    this.nameEl = this.q('.select__name');
    this.valueEl = this.q('[data-select-value]');
    this.#initialValue = this.hiddenInput?.value ?? '';
    this.#initialFilled = this.hasAttribute('data-filled');
    this.#initialLabel = this.valueEl?.textContent ?? '';
    this.#initialLabelledby = this.trigger?.getAttribute('aria-labelledby');
    this.#initialIcon = this.q('.select__icon')?.cloneNode(true) ?? null;
    this.#locked = new Set(
      this.options
        .filter(option => this.#isDisabled(option))
        .map(option => option.dataset.value)
    );
    this.activeId = null;
    this.keyboardNav = false;

    if (this.trigger && this.dataset.popup === 'dialog') {
      this.listen(this.trigger, 'click', this.onDialogTriggerClick);
      return;
    }

    if (!this.trigger || !this.modal || !this.list) {
      return;
    }

    this.list.tabIndex = -1;

    this.listen(this.trigger, 'click', this.onTriggerClick);
    this.listen(this.trigger, 'keydown', this.onTriggerKeydown);
    this.listen(this.list, 'click', this.onListClick);
    if (this.searchInput) {
      this.listen(this.searchInput, 'input', this.onSearchInput);
      this.listen(this.searchInput, 'keydown', this.onSearchKeydown);
    }
    this.listen(document, 'pointerdown', this.onDocumentPointerdown);
    if (this.dataset.fitViewport === 'true') {
      const viewport = window.visualViewport;
      this.listen(viewport ?? window, 'resize', this.onViewportChange);
      if (viewport) this.listen(viewport, 'scroll', this.onViewportChange);
      this.listen(document, 'scroll', this.onAncestorScroll, {
        capture: true,
        passive: true
      });
      this.cleanup(() => cancelAnimationFrame(this.#frame));
    }
  }

  get options() {
    return this.qa('[role="option"]');
  }

  get visibleOptions() {
    return this.options.filter(option => !option.hidden);
  }

  get enabledOptions() {
    return this.visibleOptions.filter(option => !this.#isDisabled(option));
  }

  get value() {
    return this.hiddenInput?.value ?? '';
  }

  has(value) {
    return Boolean(this.#find(value)) && !this.#locked.has(value);
  }

  optionData(value) {
    const option = this.#find(value);
    return option ? { ...option.dataset } : null;
  }

  limit(values) {
    const allowed = values == null ? null : new Set(values);
    for (const option of this.options) {
      const { value } = option.dataset;
      if (this.#locked.has(value) || (allowed && !allowed.has(value)))
        option.setAttribute('aria-disabled', 'true');
      else option.removeAttribute('aria-disabled');
    }
  }

  get isOpen() {
    return this.trigger.getAttribute('aria-expanded') === 'true';
  }

  open() {
    if (this.trigger.disabled || this.isOpen) return;
    this.modal.hidden = false;
    if (this.dataset.fitViewport === 'true') {
      const styles = getComputedStyle(this);
      this.#fitGutter =
        Number.parseFloat(styles.getPropertyValue('--select-fit-gutter')) || 0;
      this.#fitMin =
        Number.parseFloat(styles.getPropertyValue('--select-fit-min')) || 0;
      this.#fitToViewport();
    }
    this.trigger.setAttribute('aria-expanded', 'true');
    if (this.searchInput)
      this.searchInput.setAttribute('aria-expanded', 'true');
    this.setActive(this.#find(this.value) || this.enabledOptions[0] || null);
    if (this.searchInput) this.searchInput.focus();
  }

  close({ restoreFocus = false } = {}) {
    if (!this.isOpen) return;
    cancelAnimationFrame(this.#frame);
    this.modal.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    if (this.searchInput)
      this.searchInput.setAttribute('aria-expanded', 'false');
    this.setActive(null);
    if (this.searchInput && this.searchInput.value) {
      this.searchInput.value = '';
      this.onSearchInput();
    }
    if (restoreFocus) this.trigger.focus();
  }

  setActive(option) {
    for (const item of this.options) item.removeAttribute('data-active');
    this.activeId = option ? option.id : null;
    if (option) {
      if (this.keyboardNav) option.setAttribute('data-active', 'true');
      option.scrollIntoView({ block: 'nearest' });
    }
    const owner = this.searchInput || this.trigger;
    if (this.activeId)
      owner.setAttribute('aria-activedescendant', this.activeId);
    else owner.removeAttribute('aria-activedescendant');
  }

  moveActive(delta) {
    const enabled = this.enabledOptions;
    if (!enabled.length) return;
    const currentIndex = enabled.findIndex(
      option => option.id === this.activeId
    );
    let nextIndex = currentIndex + delta;
    if (nextIndex < 0) nextIndex = enabled.length - 1;
    if (nextIndex >= enabled.length) nextIndex = 0;
    this.setActive(enabled[nextIndex]);
  }

  run(action) {
    this.#action = action;
    return this;
  }

  setExpanded(open) {
    this.trigger?.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  onDialogTriggerClick() {
    runAction(this, this.#action, undefined, 'select');
  }

  onTriggerClick() {
    if (this.isOpen) this.close({ restoreFocus: true });
    else this.open();
  }

  onTriggerKeydown(event) {
    this.keyboardNav = true;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen) this.open();
        else this.moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!this.isOpen) this.open();
        else this.moveActive(-1);
        break;
      case 'Home':
        if (!this.isOpen) return;
        event.preventDefault();
        this.setActive(this.enabledOptions[0] || null);
        break;
      case 'End':
        if (!this.isOpen) return;
        event.preventDefault();
        this.setActive(this.enabledOptions.at(-1) || null);
        break;
      case 'Enter':
      case ' ':
        if (!this.isOpen) return;
        event.preventDefault();
        this.pick(this.options.find(option => option.id === this.activeId));
        break;
      case 'Escape':
        if (!this.isOpen) return;
        event.preventDefault();
        this.close({ restoreFocus: true });
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  onSearchKeydown(event) {
    this.keyboardNav = true;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(-1);
        break;
      case 'Enter':
        event.preventDefault();
        this.pick(this.options.find(option => option.id === this.activeId));
        break;
      case 'Escape':
        event.preventDefault();
        this.close({ restoreFocus: true });
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  onSearchInput() {
    this.keyboardNav = true;
    const query = this.searchInput.value.trim().toLowerCase();
    let visibleCount = 0;
    for (const option of this.options) {
      const text = `${option.textContent} ${option.dataset.search ?? ''}`;
      const match = !query || text.toLowerCase().includes(query);
      option.hidden = !match;
      if (match) visibleCount += 1;
    }
    if (this.emptyMessage) this.emptyMessage.hidden = visibleCount > 0;
    this.list.hidden = visibleCount === 0;
    if (this.isOpen) this.#scheduleFit();

    const enabled = this.enabledOptions;
    const active = enabled.find(option => option.id === this.activeId);
    this.setActive(active || enabled[0] || null);
  }

  onListClick(event) {
    const option = event.target.closest('[role="option"]');
    if (option) this.pick(option);
  }

  onDocumentPointerdown(event) {
    this.keyboardNav = false;
    if (this.isOpen && !this.contains(event.target)) this.close();
  }

  onViewportChange() {
    if (this.isOpen) this.#scheduleFit();
  }

  onAncestorScroll(event) {
    if (!this.isOpen) return;
    if (event.target instanceof Node && event.target.contains(this))
      this.#scheduleFit();
  }

  #scheduleFit() {
    if (this.dataset.fitViewport !== 'true') return;
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      if (this.isConnected && this.isOpen) this.#fitToViewport();
    });
  }

  #fitToViewport() {
    if (this.list.hidden) return;
    const viewport = window.visualViewport;
    const top = viewport ? viewport.offsetTop : 0;
    const bottom = viewport
      ? viewport.offsetTop + viewport.height
      : window.innerHeight;
    const trigger = this.trigger.getBoundingClientRect();
    const modal = this.modal.getBoundingClientRect();
    const listHeight =
      Number.parseFloat(getComputedStyle(this.list).blockSize) || 0;
    const above = this.dataset.position === 'top';
    const gap = above ? trigger.top - modal.bottom : modal.top - trigger.bottom;
    const reserved = gap + modal.height - listHeight + this.#fitGutter;
    const roomBelow = bottom - trigger.bottom - reserved;
    const roomAbove = trigger.top - top - reserved;
    const flip = roomBelow < this.#fitMin && roomAbove > roomBelow;
    const room = Math.max(0, Math.floor(flip ? roomAbove : roomBelow));
    this.dataset.position = flip ? 'top' : 'bottom';
    this.list.style.setProperty('--select-fit-room', `${room}px`);
  }

  pick(option) {
    if (!option || this.#isDisabled(option)) return;

    const { value, label } = this.#apply(option);

    this.close({ restoreFocus: true });
    this.#emit({ id: this.id, value, label });
  }

  select(value) {
    const option = this.#find(value);
    if (option) this.#apply(option, { silent: true });
  }

  reset() {
    const option = this.#find(this.#initialValue);
    if (option) this.#apply(option, { silent: true });
    else this.#restore();
    this.close();
  }

  #find(value) {
    return this.options.find(option => option.dataset.value === value);
  }

  #isDisabled(option) {
    return option.getAttribute('aria-disabled') === 'true';
  }

  #deselect() {
    for (const option of this.options)
      option.setAttribute('aria-selected', 'false');
  }

  #restore() {
    this.#deselect();
    if (this.hiddenInput) this.hiddenInput.value = this.#initialValue;
    if (this.#initialFilled) this.setAttribute('data-filled', 'true');
    else this.removeAttribute('data-filled');
    if (this.valueEl) this.valueEl.textContent = this.#initialLabel;
    if (this.#initialLabelledby)
      this.trigger.setAttribute('aria-labelledby', this.#initialLabelledby);
    this.#setIcon(this.#initialIcon?.cloneNode(true) ?? null);
  }

  #apply(option, { silent = false } = {}) {
    this.#deselect();
    option.setAttribute('aria-selected', 'true');

    const value = option.dataset.value;
    const label = option.textContent.trim();

    if (this.hiddenInput) {
      this.hiddenInput.value = value;
      if (!silent)
        this.hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (this.valueEl) this.valueEl.textContent = label;
    this.setAttribute('data-filled', 'true');
    if (this.nameEl && this.valueEl)
      this.trigger.setAttribute(
        'aria-labelledby',
        `${this.nameEl.id} ${this.valueEl.id}`
      );

    const first = option.firstElementChild;
    const leadingIcon =
      first?.classList.contains('select-tab__icon') &&
      first.childElementCount > 0
        ? first
        : null;
    let clone = null;
    if (leadingIcon) {
      clone = leadingIcon.cloneNode(true);
      clone.classList.remove('select-tab__icon');
      clone.classList.add('select__icon');
      clone.dataset.selectClonedIcon = 'true';
    }
    this.#setIcon(clone);

    return { value, label };
  }

  #setIcon(icon) {
    const current = this.q('.select__icon');
    if (icon) {
      if (current) current.replaceWith(icon);
      else this.trigger.insertBefore(icon, this.q('.select__text'));
    } else if (current?.dataset.selectClonedIcon) {
      current.remove();
    }
  }

  onChange(handler) {
    this.#handlers.add(handler);
    return () => this.#handlers.delete(handler);
  }

  onDisconnect() {
    this.#handlers.clear();
  }

  #emit(detail) {
    for (const handler of [...this.#handlers]) {
      try {
        handler(detail);
      } catch (error) {
        console.error('[select] change handler failed:', error);
      }
    }
  }
}

if (!customElements.get('select-field'))
  customElements.define('select-field', SelectField);
